import type { ReactNode } from "react";
import { useFormContext } from "react-hook-form";
import { classNames } from "@/react/helpers/twMergeWithCN";
import { APPEARANCE, useThemeContext } from "@/react/providers";
import { EmojiRenderer } from "@circle-react-shared/EmojiRenderer";
import { EMOJI_RENDERER_TYPES } from "@circle-react-shared/EmojiRenderer/constants";
import { Icon } from "@circle-react-shared/Icon";
import type { EmojiPickerAddOns } from "@circle-react-uikit/EmojiPickerV3";
import {
  CUSTOM_ICON_DARK_DATA_SET,
  CUSTOM_ICON_LIGHT_DATA_SET,
} from "@circle-react-uikit/EmojiPickerV3/data";
import type { CustomEmoji } from "@circle-react-uikit/EmojiPickerV3/data/utils";
import { customEmojiUrl } from "@circle-react-uikit/EmojiPickerV3/data/utils";
import { Form } from "@circle-react-uikit/Form";
import { DEFAULT_ICON } from "../FormEmojiPickerV3/constants";

export const EMOJI_FIELD_NAME = "emoji";

export interface EmojiPickerWrapperProps {
  className?: string;
  defaultIcon?: ReactNode;
  emojiRendererTypes?: Record<string, EmojiPickerAddOns>;
  name?: string;
  record?: Record<string, any>;
  resetToDefaultIcon?: boolean;
  shouldEmojiPickerRenderAsPortal?: boolean;
  wrapperClassName?: string;
}

export const EmojiPickerWrapper = ({
  name = "custom_emoji",
  record,
  className,
  wrapperClassName,
  shouldEmojiPickerRenderAsPortal = true,
  resetToDefaultIcon = true,
  defaultIcon = DEFAULT_ICON,
  emojiRendererTypes = EMOJI_RENDERER_TYPES,
}: EmojiPickerWrapperProps) => {
  const { isDarkAppearance, currentAppearance } = useThemeContext();
  const { setValue, watch, resetField } = useFormContext();

  const customEmoji = watch(name) as CustomEmoji;
  const recordEmoji = watch(EMOJI_FIELD_NAME);

  const handleEmojiIconUpload = ({
    status,
    response,
  }: {
    status: string;
    response: {
      signed_id: string;
      filename: string;
    } | null;
  }) => {
    if (status === "completed" && response !== null) {
      setValue(
        name,
        {
          signed_id: response.signed_id,
          filename: response.filename,
          emoji_type: emojiRendererTypes.custom,
        },
        { shouldDirty: true },
      );
    }
  };

  const handleRemoveEmoji = () => {
    if (resetToDefaultIcon) {
      return resetField(name, {
        defaultValue: defaultIcon,
      });
    }
    resetEmoji();
  };

  const resetEmoji = () => {
    resetField(EMOJI_FIELD_NAME, {
      defaultValue: null,
    });
    resetField(name, {
      defaultValue: {
        emoji: null,
        emoji_type: null,
        identifier: null,
      },
    });
  };

  const pickerAddOns: EmojiPickerAddOns[] = Object.values(
    emojiRendererTypes,
  ).filter(value => value !== emojiRendererTypes.icon);

  return (
    <Form.EmojiPickerV3
      name={name}
      target={
        <EmojiRenderer
          wrapperClassName={classNames(
            "form-control__prefix__children",
            wrapperClassName,
          )}
          className={classNames("!w-6 !p-0", className)}
          emptyIcon={<Icon type="smile" />}
          emoji={customEmoji?.emoji || recordEmoji}
          customEmojiUrl={customEmojiUrl({
            customEmoji,
            appearance: APPEARANCE.LIGHT,
            defaultUrl: record?.custom_emoji_url,
          })}
          customEmojiDarkUrl={customEmojiUrl({
            customEmoji,
            appearance: APPEARANCE.DARK,
            defaultUrl: record?.custom_emoji_dark_url,
          })}
          appearance={currentAppearance}
          isWrapperElementClickable
        />
      }
      pickerAddOns={pickerAddOns}
      customIcons={
        isDarkAppearance
          ? CUSTOM_ICON_DARK_DATA_SET
          : CUSTOM_ICON_LIGHT_DATA_SET
      }
      onRemoveEmoji={handleRemoveEmoji}
      handleCustomIconUpload={handleEmojiIconUpload}
      shouldEmojiPickerRenderAsPortal={shouldEmojiPickerRenderAsPortal}
    />
  );
};
